import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, Award } from 'lucide-react';

export default function AnalyticsPage() {
  const { t, getProductName } = useLanguage();
  const [overview, setOverview] = useState({});
  const [revenue, setRevenue] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    try {
      const [o, r, c, p] = await Promise.all([
        axios.get(`${API}/analytics/overview`),
        axios.get(`${API}/analytics/revenue?days=${days}`),
        axios.get(`${API}/analytics/top-customers`),
        axios.get(`${API}/analytics/top-products`),
      ]);
      setOverview(o.data);
      setRevenue(r.data);
      setTopCustomers(c.data);
      setTopProducts(p.data);
    } catch (err) {
      console.error(err);
    }
  };

  const formatCurrency = (val) => `₩${Number(val || 0).toLocaleString('ko-KR')}`;
  const formatCompact = (val) => {
    const n = Number(val || 0);
    if (n >= 1000000) return `₩${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `₩${(n / 1000).toFixed(0)}K`;
    return `₩${n}`;
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Аналитика</h1>
        <p className="text-gray-500 mt-1">Бизнес-показатели и тренды</p>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Выручка месяц</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 cff-text-numeric">{formatCompact(overview.month_revenue)}</p>
              {overview.growth_percent !== undefined && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${overview.growth_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {overview.growth_percent >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {overview.growth_percent >= 0 ? '+' : ''}{overview.growth_percent}%
                </p>
              )}
            </div>
            <div className="bg-emerald-50 p-2 rounded-md">
              <DollarSign className="h-5 w-5 text-emerald-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Заказы месяц</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 cff-text-numeric">{overview.total_orders_month || 0}</p>
            </div>
            <div className="bg-blue-50 p-2 rounded-md">
              <ShoppingBag className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Средний чек</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 cff-text-numeric">{formatCompact(overview.avg_order_value)}</p>
            </div>
            <div className="bg-amber-50 p-2 rounded-md">
              <Award className="h-5 w-5 text-amber-600" />
            </div>
          </div>
        </Card>

        <Card className="p-5 cff-hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Клиенты</p>
              <p className="text-2xl font-bold text-gray-900 mt-2 cff-text-numeric">{overview.total_companies || 0}</p>
            </div>
            <div className="bg-purple-50 p-2 rounded-md">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue chart */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-semibold text-gray-900">График выручки</h2>
          <div className="flex gap-2">
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-sm rounded-md ${days === d ? 'bg-[#1A1A1A] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                data-testid={`days-${d}`}
              >
                {d} дн
              </button>
            ))}
          </div>
        </div>
        {revenue.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p>Нет данных за этот период</p>
            <p className="text-xs mt-1">Создайте заказы чтобы увидеть графики</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={formatCompact} />
              <Tooltip formatter={(val) => formatCurrency(val)} />
              <Line type="monotone" dataKey="revenue" stroke="#556B2F" strokeWidth={2} dot={{ fill: '#556B2F', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Top Customers & Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🏆 Топ клиентов</h2>
          {topCustomers.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">Пока нет данных</p>
          ) : (
            <div className="space-y-2">
              {topCustomers.map((c, i) => (
                <div key={c.company_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md" data-testid={`top-customer-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{c.company_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{c.client_code} · {c.order_count} заказов</p>
                  </div>
                  <p className="font-bold cff-text-numeric text-sm">{formatCompact(c.total_revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📦 Топ товаров</h2>
          {topProducts.length === 0 ? (
            <p className="text-center py-8 text-gray-400 text-sm">Пока нет данных</p>
          ) : (
            <div className="space-y-2">
              {topProducts.map((p, i) => (
                <div key={p.product_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md" data-testid={`top-product-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{getProductName(p)}</p>
                    <p className="text-xs text-gray-500">{p.total_quantity} продано</p>
                  </div>
                  <p className="font-bold cff-text-numeric text-sm">{formatCompact(p.total_revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
